import React, { useState, useEffect, useCallback } from 'react';
import image3 from '../assets/1.webp';
import ScrollToBottomButton from '../components/ui/ScrollToBottomButton';

const hotelImages = [
  '/hotel/welcome.webp',
  '/hotel/location.webp',
  '/hotel/DSC_0301.webp',
  '/hotel/DSC_0304.webp',
  '/hotel/DSC_0305.webp',
  '/hotel/DSC_0308.webp',
  '/hotel/DSC_0309.webp',
  '/hotel/DSC_0326.webp',
  '/hotel/DSC_0327.webp',
  '/hotel/IMG_9614.webp',
];

const roomImages = [
  '/rooms/01.webp',
  '/rooms/03.webp',
  '/rooms/04.webp',
  '/rooms/07.webp',
  '/rooms/10.webp',
  '/rooms/100.webp',
  '/rooms/1000.webp',
  '/rooms/1001.webp',
  '/rooms/1002.webp',
  '/rooms/1003.webp',
  '/rooms/1004.webp',
  '/rooms/11.webp',
  '/rooms/2000.webp',
  '/rooms/2002.webp',
  '/rooms/3001.webp',
  '/rooms/4000.webp',
  '/dbl-del-quad/ddq1.webp',
  '/dbl-del-quad/ddq2.webp',
  '/dbl-del-quad/ddq3.webp',
  '/dbl-del-quad/ddq4.webp',
  '/dbl-del-quad/ddq5.webp',
  '/deluxe-double-bedded-room/ddbr1.webp',
  '/deluxe-double-bedded-room/ddbr2.webp',
  '/deluxe-double-bedded-room/ddbr3.webp',
  '/deluxe-double-bedded-room/ddbr4.webp',
  '/deluxe-double-bedded-room/ddbr5.webp',
  '/deluxe-double-bedded-room/ddbr6.webp',
  '/deluxe-double-bedded-room/ddbr7.webp',
  '/deluxe-double-bedded-room/ddbr8.webp',
  '/deluxe-double-bedded-room/ddbr9.webp',
  '/deluxe-double-bedded-room/ddbr10.webp',
  '/deluxe-double-bedded-room/ddbr11.webp',
  '/deluxe-double-bedded-room/ddbr12.webp',
  '/superior-double-bedded-room/sdbr1.webp',
  '/superior-double-bedded-room/sdbr2.webp',
  '/superior-double-bedded-room/sdbr3.webp',
  '/superior-double-bedded-room/sdbr4.webp',
  '/superior-double-bedded-room/sdbr5.webp',
  '/superior-double-bedded-room/sdbr6.webp',
  '/superior-double-bedded-room/sdbr7.webp',
  '/superior-double-bedded-room/sdbr8.webp',
  '/superior-double-bedded-room/sdbr9.webp',
  '/superior-quad-rooms/sqr1.webp',
  '/superior-quad-rooms/sqr2.webp',
  '/superior-quad-rooms/sqr3.webp',
  '/superior-quad-rooms/sqr4.webp',
];

const Gallery = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImages, setCurrentImages] = useState([]);
  const [randomHotelImage, setRandomHotelImage] = useState('');

  useEffect(() => {
    setRandomHotelImage(hotelImages[Math.floor(Math.random() * hotelImages.length)]);
  }, []);
  
  const openLightbox = (images, index) => {
    setCurrentImages(images);
    setSelectedImage(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedImage(null);
    setCurrentImages([]);
  };

  const goToNext = useCallback(() => {
    if (selectedImage === currentImages.length - 1) {
      setSelectedImage(0);
    } else {
      setSelectedImage(selectedImage + 1);
    }
  }, [selectedImage, currentImages.length]);

  const goToPrevious = useCallback(() => {
    if (selectedImage === 0) {
      setSelectedImage(currentImages.length - 1);
    } else {
      setSelectedImage(selectedImage - 1);
    }
  }, [selectedImage, currentImages.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxOpen) {
        if (e.key === 'Escape') {
          closeLightbox();
        }
        if (e.key === 'ArrowRight') {
          goToNext();
        }
        if (e.key === 'ArrowLeft') {
          goToPrevious();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxOpen, selectedImage, goToNext, goToPrevious]);

  return (
    <div className="bg-light-beige">
      <section
        className="relative h-[60vh] bg-cover bg-center flex items-center justify-center text-white"
        style={{ backgroundImage: `url(${image3})` }}
        data-aos="fade-in"
      >
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-aos="fade-up">Gallery</h1>
        </div>
        <ScrollToBottomButton targetId="gallery-content" />
      </section>

      <main id="gallery-content" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl font-semibold text-dark-green">Our Gallery</h2>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed">
              Explore the stunning visuals of our hotel and luxurious rooms.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Hotel Banner */}
            <div
              className="relative h-80 rounded-lg shadow-lg overflow-hidden cursor-pointer group"
              data-aos="fade-up"
              onClick={() => openLightbox(hotelImages, 0)}
            >
              <img
                src={randomHotelImage}
                alt="Hotel"
                className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center">
                <h3 className="text-white text-4xl font-bold">Hotel</h3>
                <p className="text-white/80 mt-2 text-lg font-medium tracking-wide">
                  {hotelImages.length} Photos
                </p>
              </div>
            </div>

            {/* Rooms Banner */}
            <div
              className="relative h-80 rounded-lg shadow-lg overflow-hidden cursor-pointer group"
              data-aos="fade-up"
              data-aos-delay="100"
              onClick={() => openLightbox(roomImages, 0)}
            >
              <img
                src="/rooms/1000.webp"
                alt="Rooms"
                className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center">
                <h3 className="text-white text-4xl font-bold">Rooms</h3>
                <p className="text-white/80 mt-2 text-lg font-medium tracking-wide">
                  {roomImages.length} Photos
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {lightboxOpen && selectedImage !== null && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black bg-opacity-90" onClick={closeLightbox}>
          <div className="relative max-w-5xl w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={currentImages[selectedImage]}
              alt={`Gallery ${selectedImage + 1}`}
              className="max-w-full max-h-[90vh] object-contain mx-auto rounded-lg"
            />
            <button
              onClick={closeLightbox}
              className="absolute top-2 right-2 h-10 w-10 flex items-center justify-center bg-white/20 rounded-full text-2xl hover:bg-white/40 transition-colors"
              style={{ color: '#0F3D2E' }}
            >
              &times;
            </button>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 p-3 rounded-full text-2xl hover:bg-white/40 transition-colors"
              style={{ color: '#0F3D2E' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 p-3 rounded-full text-2xl hover:bg-white/40 transition-colors"
              style={{ color: '#0F3D2E' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;